export const account = {
  seo: {
    login: {
      title: 'Login | LensBank',
      description: 'Log in to the LensBank member portal to view your orders, manage your profile, and book equipment.'
    },
    register: {
      title: 'Register | LensBank',
      description: 'Create a LensBank account to enjoy convenient equipment booking and exclusive benefits.'
    },
    profile: {
      title: 'Profile | LensBank Account',
      description: 'Manage your personal information, contact details, and preferences.'
    },
    orders: {
      title: 'My Orders | LensBank Account',
      description: 'View your equipment rental orders and history.'
    },
    forgotPassword: {
      title: 'Forgot Password | LensBank',
      description: 'Reset your LensBank account password.'
    }
  },
  login: {
    title: 'Member Login',
    email: 'Email',
    password: 'Password',
    rememberMe: 'Remember Me',
    forgotPassword: 'Forgot Password?',
    loginButton: 'Log In',
    loggingIn: 'Logging in...',
    noAccount: 'Don\'t have an account?',
    register: 'Sign Up',
    emailPlaceholder: 'Enter your email',
    passwordPlaceholder: 'Enter your password'
  },
  register: {
    title: 'Create Account',
    name: 'Full Name',
    email: 'Email',
    phone: 'Phone',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    company: 'Company Name (optional)',
    vatNumber: 'VAT Number (optional)',
    registerButton: 'Register',
    registering: 'Registering...',
    hasAccount: 'Already have an account?',
    login: 'Log In',
    agree: 'I agree to the',
    terms: 'Terms of Service',
    and: 'and',
    privacy: 'Privacy Policy',
    success: {
      title: 'Registration Successful!',
      message: 'A verification email has been sent to your address',
      checkEmail: 'Please check your inbox and click the verification link to activate your account',
      backToLogin: 'Back to Login'
    }
  },
  forgotPassword: {
    title: 'Forgot Password',
    description: 'Enter your email and we\'ll send you a link to reset your password',
    email: 'Email',
    emailPlaceholder: 'Enter your email',
    sendButton: 'Send Reset Link',
    sending: 'Sending...',
    backToLogin: 'Back to Login',
    success: {
      title: 'Reset Request Sent!',
      message: 'If this email is registered, we will send a password reset link. Please check your inbox and spam folder.',
      checkEmail: 'The link is valid for 1 hour. Please act promptly.',
      backToLogin: 'Back to Login'
    }
  },
  resetPassword: {
    title: 'Reset Password',
    description: 'Enter your new password and confirm it',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    submitButton: 'Confirm Reset Password',
    submitting: 'Resetting...',
    successTitle: 'Password Reset!',
    successMessage: 'Your password has been updated. Please log in with your new password.',
    backToLogin: 'Back to Login',
    invalidOrExpired: 'The reset link is invalid or has expired. Please request a new password reset.',
    errorTitle: 'Unable to Reset Password',
    errorMessage: 'The verification link is invalid or has expired.'
  },
  errors: {
    invalidEmail: 'Please enter a valid email address',
    emailRequired: 'Email is required',
    passwordRequired: 'Password is required',
    passwordTooShort: 'Password must be at least 8 characters',
    passwordMismatch: 'Passwords do not match',
    nameRequired: 'Name is required',
    phoneRequired: 'Phone is required',
    invalidCredentials: 'Incorrect email or password',
    emailAlreadyExists: 'This email is already registered',
    userNotFound: 'Account not found',
    networkError: 'Network error, please try again later',
    unknownError: 'An unknown error occurred, please try again',
    agreeTerms: 'Please agree to the Terms of Service and Privacy Policy'
  },
  profile: {
    title: 'Profile',
    subtitle: 'Manage your personal information and account settings',
    basicInfo: 'Basic Information',
    name: 'Name',
    email: 'Email',
    emailReadonly: 'Email cannot be changed',
    phone: 'Phone',
    company: 'Company Name',
    vatNumber: 'VAT Number',
    preferredLocation: 'Preferred Location',
    selectLocation: 'Select a location',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    save: 'Save Changes',
    saving: 'Saving...',
    cancel: 'Cancel',
    edit: 'Edit Profile',
    security: 'Account Security',
    password: 'Password',
    change: 'Change',
    changePasswordNote: 'To change your password, please use the Forgot Password page',
    updateSuccess: 'Profile updated successfully',
    updateError: 'Update failed, please try again',
    adminAccountNotice: 'This account is a highest-privilege admin account. For security reasons, it cannot be used as a front-end rental account. Please use a general customer account to test or place orders.',
    staffAccountNotice: 'You are currently logged in as a LensBank staff account. To use front-end rental features, please create your personal rental profile first. This data is for front-end rental only and will not affect your backend permissions.',
    staffAccountReady: 'You are currently logged in as a LensBank staff account. Your front-end rental profile is ready. You can use front-end rental features normally, and your backend permissions remain unaffected.',
    createRentalProfile: 'Edit and create front-end rental profile'
  },
  orders: {
    title: 'My Orders',
    subtitle: 'View and manage your equipment rental orders',
    orderNumber: 'Order No.',
    date: 'Order Date',
    status: 'Status',
    total: 'Total',
    action: 'Action',
    viewDetails: 'View Details',
    noOrders: 'No orders yet',
    allOrders: 'All Orders',
    pending: 'Pending',
    approved: 'Confirmed',
    completed: 'Completed',
    statusPending: 'Pending',
    statusApproved: 'Confirmed',
    statusNeedInfo: 'Info Needed',
    statusCompleted: 'Completed',
    statusCancelled: 'Cancelled',
    backToOrders: 'Back to Orders',
    orderDetails: 'Order Details',
    rentalPeriod: 'Rental Period',
    pickupLocation: 'Pickup Location',
    equipmentList: 'Equipment List',
    quantity: 'Qty',
    days: 'days',
    totalPrice: 'Total',
    customerNotes: 'Customer Notes',
    adminNotes: 'Admin Notes',
    startBrowsing: 'Browse Equipment',
    statuses: {
      draft: 'Draft',
      pending: 'Pending',
      need_info: 'Info Needed',
      approved: 'Confirmed',
      rejected: 'Rejected',
      completed: 'Completed',
      cancelled: 'Cancelled'
    },
    detail: {
      title: 'Order Details',
      orderInfo: 'Order Information',
      orderNumber: 'Order No.',
      orderDate: 'Order Date',
      status: 'Status',
      rentalInfo: 'Rental Information',
      pickupDate: 'Pickup Date',
      returnDate: 'Return Date',
      rentalDays: 'Rental Days',
      locations: 'Pickup & Return',
      pickupLocation: 'Pickup Location',
      returnLocation: 'Return Location',
      rentalPeriod: 'Rental Period',
      items: 'Order Items',
      equipment: 'Equipment',
      quantity: 'Qty',
      price: 'Price',
      subtotal: 'Subtotal',
      total: 'Total',
      notes: 'Notes',
      adminNotes: 'Admin Notes',
      cancel: 'Cancel Order',
      back: 'Back to List'
    }
  },
  nav: {
    profile: 'Profile',
    orders: 'My Orders',
    cart: 'Booking Cart',
    logout: 'Logout'
  },
  cart: {
    title: 'Booking Cart',
    subtitle: 'Review your equipment booking list',
    empty: 'Your booking cart is empty',
    emptyDescription: 'Browse equipment and add items to your cart',
    startBrowsing: 'Browse Equipment',
    days: 'days',
    quantity: 'Qty',
    remove: 'Remove',
    removeConfirm: 'Remove this item?',
    removeSuccess: 'Item removed from cart',
    summary: 'Order Summary',
    subtotal: 'Rental Subtotal',
    deposit: 'Deposit',
    total: 'Total',
    depositNote: 'Deposit will be refunded upon equipment return',
    proceedToCheckout: 'Proceed to Checkout',
    continueBooking: 'Continue Shopping',
    loginRequired: 'Please log in to proceed to checkout'
  },
  checkout: {
    title: 'Checkout',
    subtitle: 'Enter rental details and complete your booking',
    loginRequired: 'Please log in to proceed to checkout',
    rentalInfo: 'Rental Information',
    startDate: 'Pickup Date',
    endDate: 'Return Date',
    pickupLocation: 'Pickup Location',
    selectLocation: 'Select a pickup location',
    rentalDays: 'Days',
    days: 'days',
    equipmentList: 'Equipment List',
    quantity: 'Qty',
    projectInfo: 'Project Information',
    projectType: 'Project Type',
    projectTypePlaceholder: 'e.g. commercial photography, wedding, film production',
    shootingLocation: 'Shooting Location',
    shootingLocationPlaceholder: 'e.g. Taipei, outdoor shoot',
    specialRequirements: 'Special Requirements',
    specialRequirementsPlaceholder: 'Let us know any special needs or notes',
    orderSummary: 'Order Summary',
    equipmentSubtotal: 'Unit Total',
    subtotal: 'Subtotal',
    discount: 'Discount',
    rentalTotal: 'Rental Total',
    deposit: 'Deposit',
    total: 'Total',
    depositNote: 'Deposit will be refunded upon equipment return',
    submitOrder: 'Confirm Booking',
    submitting: 'Submitting...',
    backToCart: 'Back to Cart',
    agreeTerms: 'I have read and agree to the',
    rentalTerms: 'Rental Terms',
    fillRequired: 'Please fill in all required fields',
    agreeTermsRequired: 'Please agree to the rental terms',
    success: {
      title: 'Booking Confirmed!',
      message: 'Thank you for your booking',
      description: 'We\'ve received your booking and will contact you shortly to confirm',
      orderNumber: 'Order Number',
      viewOrders: 'View Orders',
      backToHome: 'Back to Home'
    },
    successModal: {
      title: 'Order Submitted',
      message: 'Your order has been submitted, but is not yet confirmed.\nAfter the pickup store confirms inventory, we will notify you by LINE or Email.\nYou can also check the latest status in "My Orders".',
      viewOrdersButton: 'View My Orders',
      continueBrowsingButton: 'Continue Browsing'
    },
    adminAccountBlocked: 'This account is a highest-privilege admin account. For security reasons, it cannot be used to place front-end rental orders. Please use a general customer account.',
    staffNeedProfile: 'Please create your front-end rental profile before submitting an order.'
  },
  confirmingEmail: 'Confirming your email...',
  pleaseWait: 'Please wait',
  confirmSuccess: 'Confirmed!',
  emailConfirmed: 'Your email has been confirmed successfully. Welcome!',
  confirmFailed: 'Confirmation Failed',
  emailConfirmError: 'The confirmation link is invalid or has expired. Please register again.',
  redirectingToHome: 'Redirecting to home...',
  backToLogin: 'Back to Login'
};
